import { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigation } from '@react-navigation/native';
import i18n from 'i18n-js';

import { detectEnvironment } from '@api/index';
import { Feedback, VERSION } from '@helpers';
import type { RootState } from '@store/types';

export interface UseManageSupportReturn {
  backend: string | false;
  askForHelpLine: { name: string; onClick: () => void }[];
  feedbackItems: { name: string; value: string; onClick: () => void }[];
  goToSecret: () => void;
  appVersion: string;
  backendVersion: string | undefined;
  minConnectorVersion: string | undefined;
  actualConnectorVersion: string | undefined;
}

export const useManageSupport = (): UseManageSupportReturn => {
  const navigation = useNavigation();

  const versions = useSelector((state: RootState) => state.notification?.versions);
  const connectorDetails = useSelector((state: RootState) => state.connector?.connectorDetails);

  const [backend, setBackend] = useState<string | false>(false);
  const secretScreenRef = useRef(0);

  useEffect(() => {
    const env = detectEnvironment();
    const envList: Record<string, string> = {
      dev: 'Development',
      qa: 'QA',
      stg: 'Staging',
      prod: 'Production',
    };
    setBackend(envList[env]);
  }, []);

  const askForHelpLine = [
    {
      name: i18n.t('Ask for Help'),
      onClick: () => Feedback({ type: 'help' }),
    },
  ];

  const feedbackItems = [
    {
      name: i18n.t('Bug Report'),
      value: i18n.t('Something didn’t work'),
      onClick: () => Feedback({ type: 'bug' }),
    },
    {
      name: i18n.t('Design or Ease of Use'),
      value: i18n.t('Issues with usability'),
      onClick: () => Feedback({ type: 'design' }),
    },
    {
      name: i18n.t('Feature Request'),
      value: i18n.t('Ideas to improve the app'),
      onClick: () => Feedback({ type: 'feature' }),
    },
  ];

  const goToSecret = useCallback(() => {
    if (secretScreenRef.current === 4) {
      secretScreenRef.current = 0;
      navigation.navigate('DevInfo' as never);
    } else {
      secretScreenRef.current += 1;
    }
  }, [navigation]);

  return {
    backend,
    askForHelpLine,
    feedbackItems,
    goToSecret,
    appVersion: VERSION,
    backendVersion: versions?.version,
    minConnectorVersion: versions?.minimumVersionConnector,
    actualConnectorVersion: connectorDetails?.version,
  };
};
